import { WorkspaceSimilarity } from "./SyncSessionModel"
import { useWorkspaceStore } from "@webapp/rd/WorkspaceStore"
import { useSyncStore } from "@webapp/rd/SyncStore"
import { FileUtil } from "@webapp/util/FileUtil"
import { LoadMode } from "@webapp/util/AsyncData"
import { DateTime } from "luxon"

// Finds the user's existing workspaces that match an upload by source-file name, so a new upload
// can be offered as an update to that tree instead of creating a duplicate.
export function useWorkspaceFinder() {
    const workspaceStore = useWorkspaceStore()
    const syncStore = useSyncStore()

    // The user's existing workspaces whose source file has the same name as the given file, so a
    // re-upload can be redirected to update that tree instead of duplicating it. Filename only --
    // the content comparison happens later, on the tree's manage page during the re-sync.
    async function findWorkspacesWithFileNameAsync(fileName: string): Promise<WorkspaceMatch[]> {
        const targetName = FileUtil.fileName(fileName)
        if (!targetName) {
            return []
        }
        // Reload so a tree created earlier this session is included, not a stale cached list.
        const workspaces = await workspaceStore.getAllWorkspacesAsync(LoadMode.Reload)
        const results: WorkspaceMatch[] = []
        for (const workspace of workspaces) {
            const lastSync = syncStore.getAsyncSync(workspace.lastSyncId, LoadMode.EnsureLoaded)?.data
            // The workspace's dataFilePath is the authoritative source-file location (client-maintained).
            const filePath = workspace.dataFilePath
            const workspaceFileName = FileUtil.fileName(filePath)
            if (workspaceFileName.toLowerCase() !== targetName.toLowerCase()) {
                continue
            }
            results.push({
                workspaceId: workspace.id!,
                dataGroupId: workspace.viewGroupId,
                fileName: workspaceFileName,
                folder: FileUtil.folder(filePath),
                device: workspace.clientDeviceLabel,
                lastSyncedDate: lastSync?.createdDate,
            })
        }
        return results
    }

    return {
        findWorkspacesWithFileNameAsync,
    }
}

export interface WorkspaceMatch {
    workspaceId: string
    dataGroupId?: string
    fileName?: string
    folder?: string
    device?: string
    lastSyncedDate?: DateTime
    similarity?: WorkspaceSimilarity
}
